import React from 'react';
import { EyeOff, FileSpreadsheet, FileText, Paperclip } from 'lucide-react';
import { AttachmentParseStatus, ThreadAttachment } from './types';

/**
 * The files a supplier attached to a reply, one chip each.
 *
 * Honest by construction: the chip carries the real filename Robbie received
 * and nothing else. When the file could not be read, the chip says so in a
 * quiet second line — a supplier's quotation that arrived as a scanned TIFF is
 * still on the table, and the buyer needs to know Robbie did not read it
 * rather than assume the numbers were understood.
 *
 * Only metadata ever reaches the browser. The parsed text stays server-side:
 * it is the one place a supplier's own address could survive inside prose.
 */
interface AttachmentChipsProps {
  attachments: ThreadAttachment[];
}

/** Why a file produced no text, in the buyer's terms — never a parser code. */
const UNREAD_REASON: Record<Exclude<AttachmentParseStatus, 'PARSED'>, string> = {
  EMPTY: 'nothing to read in this one',
  UNSUPPORTED: "I can't read this file type",
  TOO_LARGE: 'too large for me to read',
  SKIPPED: "I didn't read this one",
  FAILED: "I couldn't read this file",
};

const isSpreadsheet = (attachment: ThreadAttachment) =>
  /\.(xlsx?|csv)$/i.test(attachment.filename) || /sheet|excel|csv/i.test(attachment.contentType || '');

const isDocument = (attachment: ThreadAttachment) =>
  /\.(pdf|docx?|rtf)$/i.test(attachment.filename) || /pdf|word|document/i.test(attachment.contentType || '');

/**
 * File type by name, then by content type. A file Robbie could not read is
 * drawn in muted ink whatever its type — the chip should not look confident
 * about a document nobody has actually opened.
 */
const ChipIcon: React.FC<{ attachment: ThreadAttachment; unread: boolean }> = ({ attachment, unread }) => {
  const size = 'h-[13px] w-[13px] shrink-0';
  if (unread) return <Paperclip className={`${size} text-ink-muted`} />;
  if (isSpreadsheet(attachment)) return <FileSpreadsheet className={`${size} text-success-ink`} />;
  if (isDocument(attachment)) return <FileText className={`${size} text-danger-ink`} />;
  return <Paperclip className={`${size} text-ink-muted`} />;
};

const AttachmentChips: React.FC<AttachmentChipsProps> = ({ attachments }) => {
  if (attachments.length === 0) return null;

  return (
    <div className="mt-s3 flex flex-wrap gap-s2">
      {attachments.map((attachment, idx) => {
        const status = attachment.parseStatus;
        // An absent status is not a failure — older replies carry no parse
        // result at all, and inventing one would be the dishonest move.
        const unread = Boolean(status && status !== 'PARSED');
        const reason = unread ? UNREAD_REASON[status as Exclude<AttachmentParseStatus, 'PARSED'>] : null;

        return (
          <span
            key={`${attachment.filename}-${idx}`}
            className="inline-flex max-w-full items-start gap-s2 rounded-[7px] border border-line bg-surface px-s4 py-s2"
          >
            <ChipIcon attachment={attachment} unread={unread} />
            <span className="min-w-0">
              <span className="block truncate text-meta font-semibold text-ink-body">{attachment.filename}</span>
              {reason && (
                <span className="mt-[1px] flex items-center gap-[3px] text-[10px] text-ink-muted">
                  <EyeOff className="h-[9px] w-[9px] shrink-0" />
                  {reason}
                </span>
              )}
            </span>
          </span>
        );
      })}
    </div>
  );
};

export default AttachmentChips;
